/**
 * Decide whether a sponsor transaction hash came from a dry-run (test) send
 * rather than a real on-chain broadcast.
 *
 * The worker, when running with fake transfers enabled, does not broadcast a
 * transaction. Instead it records a placeholder hash built from the current
 * time: the millisecond timestamp rendered as hexadecimal and left-padded with
 * zeros to the full 64 hex characters of a real hash. For example a send at one
 * moment produces `0x000…019ecb994873`.
 *
 * A genuine transaction hash is thirty-two effectively random bytes, so its
 * numeric value is enormous (close to two to the two-hundred-and-fifty-sixth
 * power). A placeholder is a millisecond timestamp, which is tiny by comparison
 * — far below two to the sixty-fourth power. Comparing the numeric value of the
 * hash against that threshold separates the two with no realistic chance of a
 * false positive: a real hash landing below the threshold would require its top
 * one hundred and ninety-two bits to all be zero.
 *
 * @param hash - The sponsor transaction hash to classify. A missing or
 *   malformed value is treated as not-a-test, so callers fall back to their
 *   normal real-transaction rendering.
 * @returns True when the hash is a dry-run placeholder, false otherwise.
 */
export function isTestTransactionHash(hash: string | null | undefined): boolean {
  if (!hash) {
    return false
  }

  try {
    const PLACEHOLDER_CEILING = 1n << 64n
    return BigInt(hash) < PLACEHOLDER_CEILING
  } catch {
    return false
  }
}

/**
 * Recover the moment a dry-run (test) send was recorded, decoded from its
 * placeholder hash.
 *
 * The placeholder is the millisecond timestamp of the send, so reading the hash
 * back as a number yields that timestamp. Only meaningful for hashes that
 * {@link isTestTransactionHash} classifies as tests.
 *
 * @param hash - A dry-run placeholder transaction hash.
 * @returns The send time in milliseconds since the Unix epoch, or null when the
 *   hash is not a recognisable placeholder.
 */
export function testTransactionTimestamp(hash: string | null | undefined): number | null {
  if (!isTestTransactionHash(hash)) {
    return null
  }

  return Number(BigInt(hash as string))
}
